/**
 * Project-related database operations
 */

import { supabase } from './supabaseStorage';
import type { Project, CreateProjectData, UpdateProjectData } from '../hooks/useProjects';

/**
 * Get all projects for a user
 */
export const getUserProjects = async (
  userId: string,
  includeArchived: boolean = false
): Promise<{ success: boolean; projects?: Project[]; error?: string }> => {
  try {
    console.log('🔍 [PROJECTS-DB] Fetching projects for user:', userId);

    let query = supabase
      .from('projects')
      .select('*')
      .eq('hy_user_id', userId)
      .order('created_at', { ascending: false });

    if (!includeArchived) {
      query = query.eq('is_archived', false);
    }

    const { data, error } = await query;

    if (error) throw error;

    console.log(`✅ [PROJECTS-DB] Fetched ${data?.length || 0} projects`);

    return {
      success: true,
      projects: data
    };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error fetching projects:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch projects',
      projects: []
    };
  }
};

/**
 * Create a new project
 */
export const createProject = async (
  userId: string,
  data: CreateProjectData
): Promise<{ success: boolean; project?: Project; error?: string }> => {
  try {
    console.log('➕ [PROJECTS-DB] Creating project:', data);

    const projectData = {
      hy_user_id: userId,
      project_name: data.project_name,
      project_description: data.project_description || null,
      thumbnail_url: data.thumbnail_url || null,
      project_color: data.project_color || '#6b6b70'
    };

    const { data: project, error } = await supabase
      .from('projects')
      .insert(projectData)
      .select()
      .single();

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Project created:', project.id);

    return {
      success: true,
      project
    };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error creating project:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create project'
    };
  }
};

/**
 * Update an existing project
 */
export const updateProject = async (
  projectId: string,
  userId: string,
  data: UpdateProjectData
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🔄 [PROJECTS-DB] Updating project:', projectId);

    const { error } = await supabase
      .from('projects')
      .update(data)
      .eq('id', projectId)
      .eq('hy_user_id', userId);

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Project updated:', projectId);

    return { success: true };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error updating project:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update project'
    };
  }
};

/**
 * Delete a project
 */
export const deleteProject = async (
  projectId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🗑️ [PROJECTS-DB] Deleting project:', projectId);

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
      .eq('hy_user_id', userId);

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Project deleted:', projectId);

    return { success: true };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error deleting project:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete project'
    };
  }
};

/**
 * Get a single project by ID
 */
export const getProjectById = async (
  projectId: string,
  userId: string
): Promise<{ success: boolean; project?: Project; error?: string }> => {
  try {
    console.log('🔍 [PROJECTS-DB] Fetching project:', projectId);

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .eq('hy_user_id', userId)
      .single();

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Project fetched:', projectId);

    return {
      success: true,
      project: data
    };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error fetching project:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch project'
    };
  }
};

/**
 * Move asset to a different project
 */
export const moveAssetToProject = async (
  assetId: string,
  projectId: string | null,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📦 [PROJECTS-DB] Moving asset to project:', { assetId, projectId });

    const { error } = await supabase
      .from('assets')
      .update({ project_id: projectId })
      .eq('id', assetId)
      .eq('hy_user_id', userId);

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Asset moved successfully');

    return { success: true };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error moving asset:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to move asset'
    };
  }
};

/**
 * Move multiple assets to a project
 */
export const moveAssetsToProject = async (
  assetIds: string[],
  projectId: string | null,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📦 [PROJECTS-DB] Moving multiple assets to project:', { count: assetIds.length, projectId });

    const { error } = await supabase
      .from('assets')
      .update({ project_id: projectId })
      .in('id', assetIds)
      .eq('hy_user_id', userId);

    if (error) throw error;

    console.log('✅ [PROJECTS-DB] Assets moved successfully');

    return { success: true };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error moving assets:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to move assets'
    };
  }
};

/**
 * Get assets for a specific project
 */
export const getProjectAssets = async (
  userId: string,
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<{ success: boolean; assets?: any[]; error?: string }> => {
  try {
    console.log('🔍 [PROJECTS-DB] Fetching assets for project:', projectId);

    const { data, error } = await supabase
      .from('assets')
      .select('*')
      .eq('hy_user_id', userId)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    console.log(`✅ [PROJECTS-DB] Fetched ${data?.length || 0} assets for project`);

    return {
      success: true,
      assets: data
    };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error fetching project assets:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch project assets',
      assets: []
    };
  }
};

/**
 * Get orphaned assets (assets without a project)
 */
export const getOrphanedAssets = async (
  userId: string,
  limit: number = 50,
  offset: number = 0
): Promise<{ success: boolean; assets?: any[]; error?: string }> => {
  try {
    console.log('🔍 [PROJECTS-DB] Fetching orphaned assets');

    const { data, error } = await supabase
      .from('assets')
      .select('*')
      .eq('hy_user_id', userId)
      .is('project_id', null)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    console.log(`✅ [PROJECTS-DB] Fetched ${data?.length || 0} orphaned assets`);

    return {
      success: true,
      assets: data
    };
  } catch (error) {
    console.error('❌ [PROJECTS-DB] Error fetching orphaned assets:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch orphaned assets',
      assets: []
    };
  }
};
